import type { ReactNode } from 'react';
import Header from '../Header';
import Footer from '../Footer';

interface PageShellProps {
  /** Hide the header (rare — used by auth flows). */
  noHeader?: boolean;
  /** Hide the footer (rare). */
  noFooter?: boolean;
  /** Constrain the canvas to a max width. Defaults to `7xl` (1280px). */
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl' | 'full';
  /** Extra Tailwind classes for the `main` element. */
  mainClassName?: string;
  children: ReactNode;
}

const MAX_W: Record<NonNullable<PageShellProps['maxWidth']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  '7xl': 'max-w-7xl',
  full: 'max-w-full',
};

/**
 * App-wide page shell: header + main canvas (centered, padded) + footer.
 * The canvas uses the Variant B background palette (`slate-50` / `slate-950`).
 */
export function PageShell({ noHeader = false, noFooter = false, maxWidth = '7xl', mainClassName = '', children }: PageShellProps) {
  return (
    <div className="flex min-h-screen flex-col bg-slate-50 dark:bg-canvas-dark">
      {!noHeader && <Header />}
      <main
        id="main-content"
        tabIndex={-1}
        className={`mx-auto w-full flex-1 px-4 py-7 outline-none sm:px-6 lg:px-8 ${MAX_W[maxWidth]} ${mainClassName}`}
      >
        {children}
      </main>
      {!noFooter && <Footer />}
    </div>
  );
}
